import { Link } from "react-router-dom";
import { ArrowRight, Check, Target, ShieldCheck, FileSearch } from "lucide-react";
import { FadeIn, Eyebrow } from "@/components/Reveal";
import Marquee from "@/components/Marquee";

const IMG = {
  deer: "https://images.unsplash.com/photo-1727701773949-69e701bdb680?q=85&w=1600&auto=format&fit=crop",
  outback:
    "https://images.unsplash.com/photo-1771518701790-9094cc6bd106?q=85&w=2000&auto=format&fit=crop",
  woodland:
    "/high-country.jpg",
};

const PILLARS = [
  {
    code: "SVC/01",
    icon: Target,
    title: "Feral Animal Control",
    lead: "Targeted eradication of declared pest species across rural, peri-urban and public land — day and night.",
    body: "From mobbing pigs in cropping country to foxes working lambing paddocks, we match the method to the species, the season and the sensitivity of your ground. Suppressed firearms and thermal optics keep operations discreet in noise-sensitive areas.",
    methods: [
      "Thermal & night-vision ground shooting",
      "Suppressed firearms for peri-urban work",
      "Trapping & cage programs",
      "Baiting under ACUP chemical permits",
      "Warren ripping & fumigation",
    ],
    targets: "Pigs · Deer · Foxes · Rabbits · Wild Dogs · Goats · Feral Cats",
    img: IMG.deer,
  },
  {
    code: "SVC/02",
    icon: ShieldCheck,
    title: "Farm Culling & Vermin Programs",
    lead: "Structured knockdown and maintenance programs that keep production country productive.",
    body: "One-off shoots shift numbers for a fortnight; programs shift them for good. We build seasonal culling schedules around your lambing, calving and cropping calendar, and assist with the harvest permits and tags where game is taken.",
    methods: [
      "Scheduled knockdown shoots",
      "Livestock & crop overwatch — a standing presence through lambing, calving and cropping windows",
      "Positive identification before any shot — all non-target species protected",
      "Harvest permit & tag assistance",
      "Culls under landholder authorities",
      "Ongoing maintenance contracts",
    ],
    targets: "Sheep & cattle stations · Cropping · Vineyards · Orchards",
    img: IMG.outback,
  },
  {
    code: "SVC/03",
    icon: FileSearch,
    title: "Property Assessments & Plans",
    lead: "Know your pressure before you spend a dollar on control.",
    body: "We walk your country, map the sign and score the damage — then hand you a written, prioritised 12-month integrated management plan. Ideal before buying country, after a bad season, or when the neighbours' problem becomes yours.",
    methods: [
      "On-site audit & damage mapping",
      "Species pressure scoring",
      "Written 12-month management plan",
      "Permit & compliance paperwork",
      "Neighbourhood program coordination",
    ],
    targets: "Pre-purchase due diligence · Annual planning · Insurance & audit records",
    img: IMG.woodland,
  },
];

const PROCESS = [
  ["Assess", "Walk the ground, map the sign, score the pressure."],
  ["Plan", "Method, timing and safety plan agreed in writing."],
  ["Execute", "Licensed operators, humane standards, GPS-logged nights."],
  ["Report", "Tallies, outcomes and next-season recommendations."],
];

export default function Services() {
  return (
    <main data-testid="services-page" className="pt-16">
      <section className="mx-auto max-w-7xl px-4 pb-16 pt-16 sm:px-6 lg:px-8">
        <FadeIn>
          <Eyebrow>Services · Full Breakdown</Eyebrow>
          <h1 className="mt-4 max-w-3xl font-display text-4xl font-black uppercase leading-[0.95] tracking-tight text-bone sm:text-5xl lg:text-6xl">
            Control that holds
            <br />
            <span className="text-thermal">past the first night.</span>
          </h1>
          <p className="mt-8 max-w-2xl text-base leading-relaxed text-euca sm:text-lg">
            Three service pillars, one operating standard: licensed, insured, humane — and
            reported in writing after every visit.
          </p>
        </FadeIn>
      </section>

      <Marquee items={["Assess", "Plan", "Execute", "Report", "Thermal Night Ops", "Humane Dispatch", "GPS Logged", "Written Returns"]} />

      <section className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8">
        <div className="space-y-24">
          {PILLARS.map((p, i) => (
            <FadeIn key={p.code}>
              <article
                data-testid={`pillar-${p.code.replace("/", "-").toLowerCase()}`}
                className={`grid items-start gap-10 lg:grid-cols-12 ${i % 2 === 1 ? "" : ""}`}
              >
                <div className={`lg:col-span-5 ${i % 2 === 1 ? "lg:order-2" : ""}`}>
                  <div className="relative overflow-hidden border border-thermal/15">
                    <img src={p.img} alt={p.title} className="aspect-[4/3] w-full object-cover grayscale-[0.4]" />
                    <p className="absolute left-4 top-4 bg-obsidian/80 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.3em] text-thermal backdrop-blur">
                      {p.code}
                    </p>
                  </div>
                </div>
                <div className="lg:col-span-7">
                  <p.icon className="h-7 w-7 text-thermal" />
                  <h2 className="mt-5 font-display text-2xl font-bold tracking-tight text-bone sm:text-3xl lg:text-4xl">
                    {p.title}
                  </h2>
                  <p className="mt-3 font-display text-base font-medium text-thermal sm:text-lg">{p.lead}</p>
                  <p className="mt-5 max-w-2xl text-base leading-relaxed text-euca">{p.body}</p>
                  <ul className="mt-8 grid gap-3 sm:grid-cols-2">
                    {p.methods.map((m) => (
                      <li key={m} className="flex items-start gap-3 border border-thermal/10 bg-field p-4">
                        <Check className="mt-0.5 h-4 w-4 shrink-0 text-thermal" />
                        <span className="text-sm text-bone/90">{m}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.25em] text-euca">{p.targets}</p>
                </div>
              </article>
            </FadeIn>
          ))}
        </div>
      </section>

      <section className="border-t border-thermal/15 bg-field/40" data-testid="process-strip">
        <div className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8">
          <FadeIn>
            <Eyebrow>How Every Job Runs</Eyebrow>
            <h2 className="mt-4 font-display text-2xl font-bold tracking-tight text-bone sm:text-3xl lg:text-4xl">
              Four steps, same order, every time
            </h2>
          </FadeIn>
          <div className="mt-14 grid gap-px border border-thermal/15 bg-thermal/15 sm:grid-cols-2 lg:grid-cols-4">
            {PROCESS.map(([title, body], i) => (
              <FadeIn key={title} delay={i * 0.08}>
                <div className="h-full bg-obsidian p-8">
                  <p className="font-display text-5xl font-black text-thermal/20">{`0${i + 1}`}</p>
                  <h3 className="mt-6 font-display text-lg font-bold uppercase tracking-wide text-bone">{title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-euca">{body}</p>
                </div>
              </FadeIn>
            ))}
          </div>
          <FadeIn delay={0.2} className="mt-14">
            <div className="flex flex-col items-start justify-between gap-6 border border-range/30 bg-field p-8 sm:flex-row sm:items-center">
              <p className="max-w-2xl text-sm leading-relaxed text-bone/90">
                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-range">Compliance note — </span>
                All work is conducted under Victorian firearms and pest-animal legislation, with site-specific risk
                assessments, public-place permits where required, and $20M public liability cover.
              </p>
              <Link
                to="/contact"
                data-testid="services-enquire-button"
                className="group flex shrink-0 items-center gap-2 border border-thermal bg-thermal px-6 py-3 font-mono text-xs font-medium uppercase tracking-[0.2em] text-obsidian transition-all duration-300 hover:bg-transparent hover:text-thermal active:scale-95"
              >
                Enquire Now
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
          </FadeIn>
        </div>
      </section>
    </main>
  );
}
